/**
 * Is this a failure to fetch a lazily-loaded route chunk?
 *
 * Worth separating from ordinary render errors, because it is the one with a
 * cure the user can apply. Every open tab holds an index.html naming hashed
 * chunk files; a deploy replaces those files, so the next route change
 * requests a chunk that no longer exists and `React.lazy` rejects. Reloading
 * fetches the new index.html and the error is gone for good.
 *
 * Every browser words this differently and none of it is specified, so the
 * matching is deliberately loose. The asymmetry justifies it: a false positive
 * costs a pointless reload, while a false negative sends someone to "contact
 * your administrator" for something a reload would have fixed.
 *
 * The MIME-type case is the SPA fallback showing through — the server answered
 * a missing .js file with index.html, and the browser refused to run it.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const text = `${error.name} ${error.message}`;
  return (
    /ChunkLoadError/i.test(text) ||
    /Loading chunk [\w-]+ failed/i.test(text) ||
    /Failed to fetch dynamically imported module/i.test(text) ||
    /error loading dynamically imported module/i.test(text) ||
    /Importing a module script failed/i.test(text) ||
    // Chrome has used both phrasings; Safari and Firefox word it differently
    // again. Keying on "MIME type" plus text/html covers all of them without
    // matching anything a real bug would say.
    (/MIME type/i.test(text) && /text\/html/i.test(text))
  );
}
